/**
 * Cooling-period + anomaly HÀNH VI (US-E19.F2-0201-S03 · @UC9299 @UC9301) — @gate money, config 🔴.
 *
 * THUẦN: không DB, không I/O, KHÔNG `Date.now()`, KHÔNG `Math.random()` — deterministic (cùng input ⇒
 * cùng output). Giờ (`hourLocal`) do caller truyền vào ⇒ test được, không phụ thuộc clock/timezone.
 *
 * (1) `isCoolingRequired(amountMinor, thresholdMinor)` — chi ≥ ngưỡng ⇒ HOÃN post (cooling-period,
 *     P7.2 "ma sát an ninh trên chi lớn"). So sánh BigInt, 0 float.
 *
 * (2) `isBehaviorAnomaly({...})` — lệch THÓI QUEN: giờ bất thường (mặc định 0h–5h) HOẶC bội số so
 *     median 30 ngày (mặc định ≥ 10×). Trả `reasonCode` để dựng `AnomalyAlert{kind:'behavior'}`.
 *
 * 🔴 KHÁC anomaly KẾ TOÁN (`apps/api/.../reconcile/drift.service.ts` — Σdebit≠credit / balance-cache
 * drift). Đây KHÔNG phải kiểm cân đối sổ; đây là heuristic hành vi và **chỉ để CẢNH BÁO** — KHÔNG bao
 * giờ tự chặn tiền/reverse (quyết định là của người).
 *
 * NO-FLOAT trong QUYẾT ĐỊNH: so sánh bội số bằng BigInt (`amount >= median * k`). `multipleOfMedian`
 * trả về là number CHỈ để HIỂN THỊ (làm tròn xuống) — KHÔNG dùng cho bất kỳ quyết định tiền nào.
 */

/** Khoá registry `system_settings` (override được ở Group.settings / Fund.settings). */
export const COOLING_THRESHOLD_MINOR_KEY = "cooling_threshold_minor";
export const COOLING_WINDOW_MINUTES_KEY = "cooling_window_minutes";

export const COOLING_CONFIG_KEYS = [
  COOLING_THRESHOLD_MINOR_KEY,
  COOLING_WINDOW_MINUTES_KEY,
] as const;
export type CoolingConfigKey = (typeof COOLING_CONFIG_KEYS)[number];

/**
 * DEFAULT hệ thống (khớp seed migration `20260713200000_e19_cooling_freeze_anomaly`).
 *
 * 🔴 `cooling_threshold_minor = "0"` ⇒ **COOLING TẮT MẶC ĐỊNH** (0 = không đặt ngưỡng ⇒
 * `isCoolingRequired` luôn false) ⇒ MỌI quỹ hiện hữu duyệt-chi y NGUYÊN như trước task này
 * (**0 REGRESS** — cùng nguyên tắc `expense_auto_mode_enabled: false` của E17.F1). Ma sát an ninh là
 * lựa chọn CHỦ ĐỘNG: nhóm/quỹ bật bằng cách đặt ngưỡng > 0 (gợi ý 50_000_000 minor).
 *
 * Ngưỡng = CHUỖI BigInt minor trong jsonb (B1 no-float).
 */
export const COOLING_DEFAULTS: {
  readonly cooling_threshold_minor: string;
  readonly cooling_window_minutes: number;
} = {
  cooling_threshold_minor: "0",
  cooling_window_minutes: 1440,
};

/** Ngưỡng GỢI Ý khi nhóm bật cooling (dùng cho UI/doc — KHÔNG phải default hiệu lực). */
export const COOLING_SUGGESTED_THRESHOLD_MINOR = "50000000";

/** Trần cửa sổ nguội (30 ngày) — chống config rác khổng lồ; khớp `allowed.max` migration. */
export const COOLING_WINDOW_MAX_MINUTES = 43_200;

/** Config cooling đã resolve (fund > group > system). */
export interface CoolingConfig {
  readonly thresholdMinor: bigint;
  readonly windowMinutes: number;
}

/** True nếu `value` là CHUỖI BigInt minor ≥ 0 (tiền qua jsonb/wire là string — no-float, no-number). */
export function isCoolingThresholdMinor(value: unknown): value is string {
  if (typeof value !== "string" || value.trim() === "") return false;
  if (!/^\d+$/.test(value.trim())) return false;
  try {
    return BigInt(value.trim()) >= 0n;
  } catch {
    return false;
  }
}

/** Parse chuỗi BigInt minor → bigint; bẩn ⇒ null (caller rơi tầng kế / hard-default — fail-safe). */
export function parseCoolingThresholdMinor(value: unknown): bigint | null {
  return isCoolingThresholdMinor(value) ? BigInt(value.trim()) : null;
}

/** True nếu `value` là số phút nguyên ∈ [1, COOLING_WINDOW_MAX_MINUTES]. */
export function isCoolingWindowMinutes(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= COOLING_WINDOW_MAX_MINUTES
  );
}

/**
 * 🔴 Chi ≥ ngưỡng ⇒ CẦN COOLING (HOÃN post). Biên BẰNG NHAU ⇒ true (inclusive — "từ ngưỡng trở lên").
 * Ngưỡng ≤ 0 ⇒ coi như TẮT cooling (0 nghĩa là "không đặt ngưỡng"), tránh hoãn MỌI khoản chi.
 */
export function isCoolingRequired(amountMinor: bigint, thresholdMinor: bigint): boolean {
  if (typeof amountMinor !== "bigint" || typeof thresholdMinor !== "bigint") return false;
  if (thresholdMinor <= 0n) return false;
  return amountMinor >= thresholdMinor;
}

/** Mốc auto-post = thời điểm duyệt + cửa sổ nguội. Thuần (now truyền vào — KHÔNG đọc clock). */
export function computePostAfter(now: Date, windowMinutes: number): Date {
  const minutes = isCoolingWindowMinutes(windowMinutes)
    ? windowMinutes
    : COOLING_DEFAULTS.cooling_window_minutes;
  return new Date(now.getTime() + minutes * 60_000);
}

/** Lý do cảnh báo hành vi (mirror contract `anomalyAlert.detail.reasonCode`). */
export const ANOMALY_REASON = {
  ODD_HOUR: "odd_hour",
  MULTIPLE_OF_MEDIAN: "multiple_of_median",
} as const;
export type AnomalyReasonCode = (typeof ANOMALY_REASON)[keyof typeof ANOMALY_REASON];

/** Loại cảnh báo — CHỈ 'behavior' (kế toán/drift là bảng+luồng KHÁC). */
export const ANOMALY_KIND_BEHAVIOR = "behavior" as const;

/** Ngưỡng heuristic mặc định. */
export const DEFAULT_ODD_HOUR_RANGE: readonly [number, number] = [0, 5];
export const DEFAULT_MEDIAN_MULTIPLE_THRESHOLD = 10;

export interface BehaviorAnomalyInput {
  readonly amountMinor: bigint;
  /** Median chi 30 ngày (BigInt minor). 0 ⇒ chưa đủ dữ liệu ⇒ KHÔNG kết luận bội số. */
  readonly median30dMinor: bigint;
  /** Giờ địa phương 0..23 — TRUYỀN VÀO (thuần, không đọc clock trong hàm). */
  readonly hourLocal: number;
  readonly oddHourRange?: readonly [number, number];
  readonly medianMultipleThreshold?: number;
}

export interface BehaviorAnomalyResult {
  readonly anomaly: boolean;
  readonly reasonCode?: AnomalyReasonCode;
  /** Hệ số bội (làm tròn XUỐNG) — CHỈ để hiển thị/cảnh báo, KHÔNG dùng quyết định tiền. */
  readonly multipleOfMedian?: number;
}

/**
 * Heuristic hành vi. Ưu tiên `odd_hour` (rõ ràng nhất) rồi `multiple_of_median`.
 * KHÔNG throw (job nền không được chết vì 1 input bẩn) — input bẩn ⇒ `{ anomaly: false }`.
 */
export function isBehaviorAnomaly(input: BehaviorAnomalyInput): BehaviorAnomalyResult {
  const { amountMinor, median30dMinor, hourLocal } = input;
  if (typeof amountMinor !== "bigint" || typeof median30dMinor !== "bigint") {
    return { anomaly: false };
  }
  if (!Number.isInteger(hourLocal) || hourLocal < 0 || hourLocal > 23) {
    return { anomaly: false };
  }

  const [lo, hi] = input.oddHourRange ?? DEFAULT_ODD_HOUR_RANGE;
  if (Number.isInteger(lo) && Number.isInteger(hi) && lo <= hi && hourLocal >= lo && hourLocal <= hi) {
    return { anomaly: true, reasonCode: ANOMALY_REASON.ODD_HOUR };
  }

  const k = input.medianMultipleThreshold ?? DEFAULT_MEDIAN_MULTIPLE_THRESHOLD;
  if (!Number.isInteger(k) || k <= 0) return { anomaly: false };
  // median = 0 ⇒ chưa đủ lịch sử (hoặc quỹ chưa chi) ⇒ KHÔNG kết luận (tránh cảnh báo mọi khoản chi).
  if (median30dMinor <= 0n || amountMinor <= 0n) return { anomaly: false };

  // 🔴 So sánh BigInt: amount >= median × k. KHÔNG chia float.
  if (amountMinor >= median30dMinor * BigInt(k)) {
    return {
      anomaly: true,
      reasonCode: ANOMALY_REASON.MULTIPLE_OF_MEDIAN,
      // Hệ số hiển thị = floor(amount / median) qua phép chia BigInt (không float trung gian).
      multipleOfMedian: Number(amountMinor / median30dMinor),
    };
  }

  return { anomaly: false };
}
